import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Flex,
  HStack,
  Text,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  VStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  useDisclosure,
  Spinner,
  Center,
  useMediaQuery,
} from '@chakra-ui/react';
import { useApp } from '../context/AppContext';
import ReportHeader from './ReportHeader';
import HijriDatePicker from './HijriDatePicker';
import { arabicToEnglish } from '../utils/numberConverter';
import { auditLogsService } from '../services/apiService';
import CaseNavigation from './CaseNavigation';
import moment from 'moment-hijri';

const TechnicalReport = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const { cases, samples, traces, users, loading, loadSamplesByCase, loadTracesByCase, loadUsers, loadingSamples, loadingTraces } = useApp();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [confirmationData, setConfirmationData] = useState({
    position: '',
    name: '',
    athrReportNumber: '',
    athrReportDate: '',
    additionalAttachments: [],
  });
  const [newAttachment, setNewAttachment] = useState('');
  const samplesLoadedRef = useRef(false);
  const tracesLoadingRef = useRef(false); // Track if traces are currently being loaded
  const lastLoadedCaseRef = useRef(null);
  const lastLoadedSamplesCountRef = useRef(0);
  const usersLoadedRef = useRef(false);
  
  // Check if screen is too small for report display (mobile/tablet)
  // Reports are designed for A4 print, so hide on screens smaller than md (768px)
  const [isMobile] = useMediaQuery('(max-width: 767px)');

  // Reset refs when caseId changes
  useEffect(() => {
    samplesLoadedRef.current = false;
    tracesLoadingRef.current = false;
    lastLoadedCaseRef.current = null;
    lastLoadedSamplesCountRef.current = 0;
    usersLoadedRef.current = false;
  }, [caseId]);

  // Load users if not already loaded (needed for examiners display)
  useEffect(() => {
    if (!loading && users.length === 0 && !usersLoadedRef.current) {
      usersLoadedRef.current = true;
      loadUsers(1, false, null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, users.length]);

  // Get case samples using useMemo to avoid unnecessary recalculations
  // Use a stable reference by comparing sample IDs only
  const caseSamples = useMemo(() => {
    if (!caseId) return [];
    const filtered = samples.filter(s => String(s.caseId) === String(caseId));
    // Return a stable array reference by sorting by ID
    return filtered.sort((a, b) => Number(a.id) - Number(b.id));
  }, [caseId, samples]);

  // Load samples for this case when component mounts
  useEffect(() => {
    if (!caseId) return;

    if (caseSamples.length === 0 && !samplesLoadedRef.current) {
      samplesLoadedRef.current = true;
      loadSamplesByCase(caseId);
    } else if (caseSamples.length > 0 && !samplesLoadedRef.current) {
      samplesLoadedRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, caseSamples.length]);

  // Load traces when samples are loaded for this case
  useEffect(() => {
    if (!caseId || !samplesLoadedRef.current) return;
    
    // Only process if we have samples
    if (caseSamples.length === 0) return;
    
    // Check if we've already loaded traces for this exact case and samples count
    const caseChanged = lastLoadedCaseRef.current !== caseId;
    const samplesCountChanged = lastLoadedSamplesCountRef.current !== caseSamples.length;
    
    // If case or samples count changed, we need to reload traces
    if (!caseChanged && !samplesCountChanged) {
      // Same case and same number of samples - check if we have traces
      const existingTraces = traces.filter(t => String(t.caseId) === String(caseId));
      if (existingTraces.length > 0) {
        // We have traces, no need to reload
        return;
      }
    }
    
    // Prevent concurrent execution - check and set atomically
    if (tracesLoadingRef.current) {
      return;
    }
    tracesLoadingRef.current = true;
    
    // Update refs before loading
    lastLoadedCaseRef.current = caseId;
    lastLoadedSamplesCountRef.current = caseSamples.length;
    
    // Load all traces for the case in a single request
    loadTracesByCase(caseId).finally(() => {
      tracesLoadingRef.current = false;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, caseSamples.length]); // Reload when case or samples count changes

  const reportData = useMemo(() => {
    // Ensure we compare IDs as strings (API قد يرجعها أرقام)
    const caseItem = cases.find(c => String(c.id) === String(caseId));
    if (!caseItem) return null;

    // Use caseSamples from useMemo above
    const allTraces = traces.filter(t => {
      return caseSamples.some(s => String(s.id) === String(t.sampleId));
    });

    // Get assigned examiners (users assigned to this case)
    const assignedExaminers = caseItem.assignedUsers && Array.isArray(caseItem.assignedUsers)
      ? users.filter(user => {
          const userId = String(user.id);
          return caseItem.assignedUsers.some(assignedId => String(assignedId) === userId);
        })
      : [];

    let imageCounter = 1;
    const samplesWithTraces = caseSamples.map(sample => {
      let sampleImageNumber = null;
      if (sample.sampleImage) {
        sampleImageNumber = imageCounter++;
      }

      // Ensure we compare IDs as strings
      const sampleTraces = allTraces.filter(t => String(t.sampleId) === String(sample.id));
      const tracesWithImageNumbers = sampleTraces.map(trace => {
        let traceImageNumber = null;
        if (trace.traceImage) {
          traceImageNumber = imageCounter++;
        }
        return {
          ...trace,
          imageNumber: traceImageNumber
        };
      });

      return {
        ...sample,
        sampleImageNumber,
        traces: tracesWithImageNumbers
      };
    });

    return {
      case: caseItem,
      samples: samplesWithTraces,
      totalImages: imageCounter - 1,
      examiners: assignedExaminers
    };
  }, [caseId, cases, caseSamples, traces, users]);

  // Wait for loading to complete before checking if case exists
  if (loading) {
    return null; // Or a loading spinner
  }

  // Show loader while loading samples or traces
  if (loadingSamples || loadingTraces) {
    return (
      <Box p={10} bg="white" borderRadius="md" boxShadow="md">
        <Center>
          <VStack spacing={4}>
            <Spinner size="xl" color="primary.500" thickness="4px" speed="0.65s" />
            <Text color="gray.600">
              {loadingSamples && loadingTraces 
                ? 'جاري تحميل العينات والآثار...' 
                : loadingSamples 
                ? 'جاري تحميل العينات...' 
                : 'جاري تحميل الآثار...'}
            </Text>
          </VStack>
        </Center>
      </Box>
    );
  }

  if (!reportData) {
    return (
      <Box p={6} bg="white" borderRadius="md" boxShadow="md">
        <Text mb={4}>القضية غير موجودة</Text>
        <Button 
          onClick={() => {
            // Restore last page when navigating back
            const savedPage = localStorage.getItem('cases_last_page');
            if (savedPage) {
              // Page will be restored when CaseManagement loads
            }
            navigate('/cases');
          }} 
          colorScheme="primary"
        >
          العودة إلى القضايا
        </Button>
      </Box>
    );
  }

  const handlePrint = () => {
    // Open modal to fill confirmation data before printing
    onOpen();
  };

  const handleConfirmPrint = async () => {
    // Log print action
    try {
      if (caseId) {
        await auditLogsService.logReportPrint(caseId, 'technical');
      }
    } catch (error) {
      console.error('Failed to log print action:', error);
      // Continue with print even if logging fails
    }
    
    // Close modal and print
    onClose();
    // Small delay to ensure modal is closed before printing
    setTimeout(() => {
      window.print();
    }, 100);
  };

  const surfaceTypeLabels = {
    'Porous': 'مسامي',
    'Non-Porous': 'غير مسامي'
  };

  const statusLabels = {
    'Valid': 'صالح',
    'Invalid': 'غير صالح'
  };

  const decisionLabels = {
    'Match': 'تطابق',
    'Mismatch': 'عدم تطابق'
  };

  const formatArabicListNumber = (n) => {
    try {
      return new Intl.NumberFormat('ar-SA-u-nu-arab').format(n);
    } catch (e) {
      const map = ['٠','١','٢','٣','٤','٥','٦','٧','٨','٩'];
      return String(n).split('').map(ch => map[Number(ch)] ?? ch).join('');
    }
  };

  const formatHijriFromGregorian = (gregorianDate) => {
    if (!gregorianDate) return '';
    try {
      const d = moment(gregorianDate, 'YYYY-MM-DD');
      if (!d.isValid()) return String(gregorianDate);
      return d.format('iD iMMMM iYYYY') + ' هـ';
    } catch (e) {
      return String(gregorianDate);
    }
  };

  // Show message on mobile/tablet instead of report content
  if (isMobile) {
    return (
      <>
        <CaseNavigation />
        <Container maxW="1200px" py={10}>
          <Center>
            <VStack spacing={6} textAlign="center" p={8} bg="white" borderRadius="md" boxShadow="lg">
              <Box fontSize="6xl" color="primary.500">
                <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="currentColor"/>
                </svg>
              </Box>
              <Text fontSize="xl" fontWeight="bold" color="gray.800">
                التقرير الفني
              </Text>
              <Text fontSize="md" color="gray.600" maxW="500px" lineHeight="1.8">
                هذا التقرير مصمم للعرض والطباعة على شاشة الكمبيوتر. يرجى فتح التقرير على جهاز كمبيوتر أو جهاز لوحي بحجم شاشة أكبر لعرضه وطباعته بشكل صحيح.
              </Text>
              <Button onClick={() => navigate(-1)} colorScheme="primary" size="lg" mt={4}>
                العودة إلى القضية
              </Button>
            </VStack>
          </Center>
        </Container>
      </>
    );
  }

  return (
    <>
      <CaseNavigation />
      <style>
        {`
          /* A4 dimensions: 21cm x 29.7cm */
          /* At 96dpi: 793.7px x 1119.5px */
          /* We'll use 794px width for screen display */
          
          
          @media print {
            @page {
              size: A4 portrait;
              margin-top: 1.5cm !important;
              margin-bottom: 1.5cm !important;
              margin-left: 0.3cm !important;
              margin-right: 0.3cm !important;
              background: white !important;
              @bottom-center {
                content: counter(page) " - " counter(pages);
                font-size: 10pt;
                font-family: Arial, sans-serif;
                text-align: center;
              }
            }
            html, body {
              background: white !important;
              color: black !important;
            }
            * {
              background: transparent !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            .technical-report-content {
              background: white !important;
            }
            .technical-report-content,
            .technical-report-content * {
              background: white !important;
              color: black !important;
            }
            /* Allow blue section titles to keep their color in print (override the forced black above) */
            .technical-report-content .table-header-blue,
            .technical-report-content .table-header-blue * {
              color: #0ea5e9 !important;
            }
            /* Title and full-row red overrides in print (override the forced black above) */
            .technical-report-header .report-title-red,
            .technical-report-header .report-title-red * {
              color: #dc2626 !important;
            }
            .technical-report-content tr.trace-valid-row,
            .technical-report-content tr.trace-valid-row td,
            .technical-report-content tr.trace-valid-row th,
            .technical-report-content tr.decision-match-row,
            .technical-report-content tr.decision-match-row td,
            .technical-report-content tr.decision-match-row th {
              color: #991b1b !important;
            }
            .technical-report-header {
              position: relative;
              margin: 0;
              padding: 0;
            }
            .technical-report-content {
              margin-top: 0 !important;
              padding-top: 0 !important;
            }
            /* Prevent trailing margins from pushing a blank last page */
            .technical-report-content > *:last-child {
              margin-bottom: 0 !important;
            }
            table {
              page-break-inside: auto !important;
              display: table !important;
              visibility: visible !important;
              width: 100% !important;
              border: 1px solid #000 !important;
              border-collapse: collapse !important;
            }
            thead {
              display: table-header-group !important;
            }
            tbody {
              display: table-row-group !important;
            }
            tbody tr {
              page-break-inside: avoid !important;
              display: table-row !important;
            }
            td, th {
              page-break-inside: avoid !important;
              display: table-cell !important;
              border: 1px solid #000 !important;
              padding: 8px !important;
            }
          }
        `}
      </style>
    <Container 
      maxW="900px"
      sx={{
        '@media print': {
          paddingTop: '0 !important',
          marginTop: '0 !important',
          maxWidth: '100% !important',
        },
      }}
    >
      <Flex
        justify="space-between"
        mb={6}
        sx={{
          '@media print': {
            display: 'none',
          },
        }}
      >
        <Button 
          onClick={() => {
            // Restore last page when navigating back
            const savedPage = localStorage.getItem('cases_last_page');
            if (savedPage) {
              // Page will be restored when CaseManagement loads
            }
            navigate('/cases');
          }} 
          variant="outline"
        >
          العودة إلى القضايا
        </Button>
        <Button onClick={handlePrint} colorScheme="primary">
          طباعة التقرير
        </Button>
      </Flex>

      <Box
        bg="white !important"
        p={8}
        boxShadow="md"
        borderRadius="md"
        className="technical-report-content"
        sx={{
          // Force light mode colors for the report
          bg: 'white !important',
          color: 'gray.800 !important',
          // Allow specific section headers to keep their blue color on screen
          '& .table-header-blue': {
            color: '#0ea5e9 !important',
          },
          '& .chakra-text.table-header-blue': {
            color: '#0ea5e9 !important',
          },
          // Title in the header
          '& .report-title-red': {
            color: '#dc2626 !important',
          },
          '& .chakra-text.report-title-red': {
            color: '#dc2626 !important',
          },
          // Full-row red for valid traces and match decisions
          '& tr.trace-valid-row, & tr.trace-valid-row td, & tr.trace-valid-row th': {
            color: '#991b1b !important',
          },
          '& tr.decision-match-row, & tr.decision-match-row td, & tr.decision-match-row th': {
            color: '#991b1b !important',
          },
          '& *': {
            color: 'inherit',
          },
          '& table': {
            bg: 'white !important',
            color: 'gray.800 !important',
          },
          '& th': {
            bg: 'gray.50 !important',
            color: 'gray.800 !important',
            borderColor: 'gray.200 !important',
          },
          '& td': {
            bg: 'white !important',
            color: 'gray.800 !important',
            borderColor: 'gray.200 !important',
          },
          '& .chakra-text': {
            color: 'gray.800 !important',
          },
          '@media print': {
            boxShadow: 'none',
            p: 0,
            m: 0,
            display: 'block',
            position: 'relative',
            bg: 'white !important',
            color: 'black !important',
          },
        }}
      >
        <Box
          className="technical-report-header"
          mb={8}
          sx={{
            '@media print': {
              position: 'relative',
              marginTop: '0 !important',
              marginBottom: '20px !important',
              paddingTop: '0 !important',
              paddingBottom: '0 !important',
            },
          }}
        >
          <ReportHeader
            date={new Date().toLocaleDateString('ar-SA', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              calendar: 'islamic-umalqura'
            })}
            title="تقرير الفحوص الفنية للبصمات"
            caseNumber={reportData.case.caseNumber}
          />
        </Box>
        <Box 
          mb={8}
          sx={{
            '@media print': {
              marginBottom: '24px !important',
              display: 'block !important',
              visibility: 'visible !important',
            },
          }}
        >
          <Text
            as="h2"
            fontSize="sm"
            fontWeight="bold"
            mb={4}
            pb={2}
            borderBottom="1px solid"
            borderColor="gray.300"
            display="block"
            color="sky.500"
            className="table-header-blue"
            style={{ color: '#0ea5e9' }}
            sx={{
              '@media print': {
                display: 'block !important',
                visibility: 'visible !important',
                opacity: '1 !important',
                color: '#0ea5e9 !important',
                fontSize: '14pt !important',
                fontWeight: 'bold !important',
                marginTop: '0 !important',
                marginBottom: '16px !important',
                paddingTop: '0 !important',
                paddingBottom: '8px !important',
                borderBottom: '1px solid #000 !important',
                pageBreakAfter: 'avoid',
              },
            }}
          >
            معلومات القضية
          </Text>
          <Table variant="simple" size="sm" sx={{
            tableLayout: 'auto',
            width: '100%',
            border: '1px solid',
            borderColor: 'gray.300',
            '@media print': {
              tableLayout: 'auto',
              width: '100%',
              border: '1px solid #000 !important',
            },
            '& td, & th': {
              border: '1px solid',
              borderColor: 'gray.300',
              '@media print': {
                border: '1px solid #000 !important',
              },
            },
          }}>
            <Tbody>
              <Tr>
                <Td 
                  fontWeight="semibold" 
                  bg="gray.50"
                  sx={{
                    whiteSpace: 'nowrap',
                    width: 'auto',
                    minWidth: 'fit-content',
                    '@media print': {
                      whiteSpace: 'nowrap !important',
                      width: 'auto !important',
                      minWidth: 'fit-content !important',
                    },
                  }}
                >
                  رقم خطاب الإحالة:
                </Td>
                <Td sx={{
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  wordBreak: 'break-word',
                  whiteSpace: 'normal',
                  maxWidth: '70%',
                  overflow: 'hidden',
                  '@media print': {
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                    maxWidth: '70%',
                    overflow: 'hidden',
                  },
                }}>{reportData.case.referenceLetterNumber || 'غير محدد'}</Td>
              </Tr>
              <Tr>
                <Td 
                  fontWeight="semibold" 
                  bg="gray.50"
                  sx={{
                    whiteSpace: 'nowrap',
                    width: 'auto',
                    minWidth: 'fit-content',
                    '@media print': {
                      whiteSpace: 'nowrap !important',
                      width: 'auto !important',
                      minWidth: 'fit-content !important',
                    },
                  }}
                >
                  الجهة الطالبة:
                </Td>
                <Td sx={{
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  wordBreak: 'break-word',
                  whiteSpace: 'normal',
                  maxWidth: '70%',
                  overflow: 'hidden',
                  '@media print': {
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                    maxWidth: '70%',
                    overflow: 'hidden',
                  },
                }}>{reportData.case.requestingAuthority || 'غير محدد'}</Td>
              </Tr>
              <Tr>
                <Td 
                  fontWeight="semibold" 
                  bg="gray.50"
                  sx={{
                    whiteSpace: 'nowrap',
                    width: 'auto',
                    minWidth: 'fit-content',
                    '@media print': {
                      whiteSpace: 'nowrap !important',
                      width: 'auto !important',
                      minWidth: 'fit-content !important',
                    },
                  }}
                >
                  تاريخ الخطاب:
                </Td>
                <Td sx={{
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  wordBreak: 'break-word',
                  whiteSpace: 'normal',
                  maxWidth: '70%',
                  overflow: 'hidden',
                  '@media print': {
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                    maxWidth: '70%',
                    overflow: 'hidden',
                  },
                }}>{reportData.case.letterDateHijriFormatted || 'غير محدد'}</Td>
              </Tr>
              <Tr>
                <Td 
                  fontWeight="semibold" 
                  bg="gray.50"
                  sx={{
                    whiteSpace: 'nowrap',
                    width: 'auto',
                    minWidth: 'fit-content',
                    '@media print': {
                      whiteSpace: 'nowrap !important',
                      width: 'auto !important',
                      minWidth: 'fit-content !important',
                    },
                  }}
                >
                  موضوع القضية:
                </Td>
                <Td sx={{
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  wordBreak: 'break-word',
                  whiteSpace: 'normal',
                  maxWidth: '70%',
                  overflow: 'hidden',
                  '@media print': {
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                    maxWidth: '70%',
                    overflow: 'hidden',
                  },
                }}>{reportData.case.caseSubject || 'غير محدد'}</Td>
              </Tr>
            </Tbody>
          </Table>
        </Box>

        <Box mb={8}>
          <Text
            as="h2"
            fontSize="sm"
            fontWeight="bold"
            mb={4}
            pb={2}
            borderBottom="1px solid"
            borderColor="gray.300"
            display="block"
            className="table-header-blue"
            style={{ color: '#0ea5e9' }}
            sx={{
              color: '#0ea5e9 !important',
              WebkitPrintColorAdjust: 'exact',
              printColorAdjust: 'exact',
              '@media print': {
                display: 'block !important',
                visibility: 'visible !important',
                opacity: '1 !important',
                color: '#0ea5e9 !important',
                fontSize: '14pt !important',
                fontWeight: 'bold !important',
                marginBottom: '16px !important',
                paddingBottom: '8px !important',
                borderBottom: '1px solid #000 !important',
                pageBreakAfter: 'avoid',
                WebkitPrintColorAdjust: 'exact !important',
                printColorAdjust: 'exact !important',
              },
            }}
          >
            العينات الواردة للفحص وطرق فحصها
          </Text>
          {reportData.samples.length === 0 ? (
            <Text textAlign="center" color="gray.500" py={8} fontStyle="italic">
              لا توجد عينات مسجلة
            </Text>
          ) : (
            <Table variant="simple" size="sm" sx={{
              tableLayout: 'auto',
              width: '100%',
              border: '1px solid',
              borderColor: 'gray.300',
              '@media screen': {
                tableLayout: 'auto',
                width: '100%',
                border: '1px solid',
                borderColor: 'gray.300',
              },
              '@media print': {
                tableLayout: 'auto',
                width: '100%',
                border: '1px solid #000 !important',
              },
              '& td, & th': {
                border: '1px solid',
                borderColor: 'gray.300',
                '@media print': {
                  border: '1px solid #000 !important',
                },
              },
            }}>
              <Thead>
                <Tr>
                  <Th whiteSpace="nowrap" textAlign="center" fontWeight="bold" sx={{ width: 'auto', minWidth: 'fit-content' }}>رقم العينة</Th>
                  <Th textAlign="center" fontWeight="bold">الوصف</Th>
                  <Th textAlign="center" fontWeight="bold">نوع السطح</Th>
                  <Th textAlign="center" fontWeight="bold">مادة الفحص</Th>
                  <Th whiteSpace="nowrap" textAlign="center" fontWeight="bold">رقم الصورة</Th>
                </Tr>
              </Thead>
              <Tbody>
                {reportData.samples.map((sample) => (
                  <Tr key={sample.id}>
                    <Td sx={{
                      whiteSpace: 'nowrap',
                      textAlign: 'center',
                      width: 'auto',
                      minWidth: 'fit-content',
                      padding: '8px 12px',
                      '@media print': {
                        whiteSpace: 'nowrap',
                        textAlign: 'center',
                        width: 'auto',
                        minWidth: 'fit-content',
                        padding: '4px 8px',
                      },
                    }} dir="ltr">{sample.sampleNumber}</Td>
                    <Td sx={{
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      whiteSpace: 'normal',
                      textAlign: 'right',
                      width: '35%',
                      '@media screen': {
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        whiteSpace: 'normal',
                        textAlign: 'right',
                        width: '35%',
                      },
                      '@media print': {
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        whiteSpace: 'normal',
                        textAlign: 'right',
                      },
                    }}>{sample.description || 'غير محدد'}</Td>
                    <Td sx={{
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      whiteSpace: 'normal',
                      textAlign: 'center',
                      '@media screen': {
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        whiteSpace: 'normal',
                      },
                      '@media print': {
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        whiteSpace: 'normal',
                        textAlign: 'center',
                      },
                    }}>{surfaceTypeLabels[sample.surfaceType] || sample.surfaceType}</Td>
                    <Td sx={{
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      whiteSpace: 'normal',
                      textAlign: 'center',
                      '@media print': {
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        whiteSpace: 'normal',
                        textAlign: 'center',
                      },
                    }}>{sample.examinationMaterial || 'غير محدد'}</Td>
                    <Td sx={{
                      whiteSpace: 'nowrap',
                      textAlign: 'center',
                      width: 'auto',
                      '@media print': {
                        whiteSpace: 'nowrap',
                        textAlign: 'center',
                        width: 'auto',
                      },
                    }}>
                      {sample.sampleImageNumber || '-'}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </Box>

        <Box mb={8}>
          <Text
            as="h2"
            fontSize="sm"
            fontWeight="bold"
            mb={4}
            pb={2}
            borderBottom="1px solid"
            borderColor="gray.300"
            display="block"
            color="gray.800"
            sx={{
              '@media print': {
                display: 'block !important',
                visibility: 'visible !important',
                opacity: '1 !important',
                color: '#000000 !important',
                fontSize: '14pt !important',
                fontWeight: 'bold !important',
                marginBottom: '16px !important',
                paddingBottom: '8px !important',
                borderBottom: '1px solid #000 !important',
                pageBreakAfter: 'avoid',
              },
            }}
          >
            <Text
              as="span"
              className="table-header-blue"
              style={{ color: '#0ea5e9' }}
              sx={{
                color: '#0ea5e9 !important',
              }}
            >
              نتيجة الفحص:
            </Text>
            <Text
              as="span"
              fontSize="xs"
              fontWeight="normal"
              color="gray.800"
              sx={{
                '@media print': {
                  fontSize: '10pt !important',
                  fontWeight: 'normal !important',
                  color: '#000000 !important',
                },
              }}
            >
              {' '}
              بعد فحص العينات بإستخدام أجهزة الفحص والمواد الكيمائية المظهرة للبصمات اتضح مايلي
            </Text>
          </Text>
          {reportData.samples.every(s => s.traces.length === 0) ? (
            <Text textAlign="center" color="gray.500" py={8} fontStyle="italic">
              لا توجد آثار مسجلة
            </Text>
          ) : (
            <Table variant="simple" size="sm" sx={{
              tableLayout: 'fixed',
              width: '100%',
              border: '1px solid',
              borderColor: 'gray.300',
              '@media screen': {
                tableLayout: 'fixed',
                width: '100%',
                border: '1px solid',
                borderColor: 'gray.300',
              },
              '@media print': {
                tableLayout: 'fixed',
                width: '100%',
                border: '1px solid #000 !important',
              },
              '& td, & th': {
                border: '1px solid',
                borderColor: 'gray.300',
                '@media print': {
                  border: '1px solid #000 !important',
                },
              },
            }}>
              <Thead>
                <Tr>
                  <Th width="28%" textAlign="center" fontWeight="bold">رقم العينة</Th>
                  <Th width="32%" textAlign="center" fontWeight="bold">رقم الأثر</Th>
                  <Th width="25%" textAlign="center" fontWeight="bold">حالة الأثر</Th>
                  <Th width="15%" whiteSpace="nowrap" textAlign="center" fontWeight="bold">رقم الصورة</Th>
                </Tr>
              </Thead>
              <Tbody>
                {reportData.samples.flatMap((sample) => {
                  const traces = sample.traces || [];
                  if (traces.length === 0) {
                    return [
                      <Tr key={`${sample.id}-no-traces`}>
                        <Td
                          rowSpan={1}
                          textAlign="center"
                          verticalAlign="middle"
                          dir="ltr"
                          sx={{
                            fontWeight: 'bold',
                            '@media print': {
                              color: '#000000 !important',
                            },
                          }}
                        >
                          {sample.sampleNumber}
                        </Td>
                        <Td textAlign="center">لايوجد</Td>
                        <Td textAlign="center">لايوجد</Td>
                        <Td textAlign="center">لايوجد</Td>
                      </Tr>,
                    ];
                  }

                  // If the sample has any valid trace, color the merged sample cell red (optional)
                  const sampleHasValidTrace = traces.some((t) => t.status === 'Valid');

                  return traces.map((trace, traceIndex) => (
                    <Tr
                      key={`${sample.id}-${trace.id}`}
                      className={trace.status === 'Valid' ? 'trace-valid-row' : undefined}
                      sx={{
                        color: trace.status === 'Valid' ? '#991b1b' : 'inherit',
                        '@media print': {
                          color: trace.status === 'Valid' ? '#991b1b !important' : '#000000',
                        },
                      }}
                    >
                      {traceIndex === 0 && (
                        <Td
                          rowSpan={traces.length}
                          textAlign="center"
                          verticalAlign="middle"
                          dir="ltr"
                          sx={{
                            fontWeight: 'bold',
                            color: sampleHasValidTrace ? '#991b1b !important' : 'gray.800',
                            '@media print': {
                              color: sampleHasValidTrace ? '#991b1b !important' : '#000000',
                            },
                          }}
                        >
                          {sample.sampleNumber}
                        </Td>
                      )}

                      <Td sx={{
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        whiteSpace: 'normal',
                        textAlign: 'center',
                        '@media print': {
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word',
                          whiteSpace: 'normal',
                          textAlign: 'center',
                        },
                      }}>{trace.traceNumber}</Td>
                      <Td sx={{
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        whiteSpace: 'normal',
                        textAlign: 'center',
                        width: '25%',
                        '@media screen': {
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word',
                          whiteSpace: 'normal',
                          textAlign: 'center',
                          width: '25%',
                        },
                        '@media print': {
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word',
                          whiteSpace: 'normal',
                          textAlign: 'center',
                        },
                      }}>{statusLabels[trace.status] || trace.status}</Td>
                      <Td sx={{
                        whiteSpace: 'nowrap',
                        textAlign: 'center',
                        width: '15%',
                        '@media print': {
                          whiteSpace: 'nowrap',
                          textAlign: 'center',
                          width: '15%',
                        },
                      }}>
                        {trace.imageNumber || '-'}
                      </Td>
                    </Tr>
                  ));
                })}
              </Tbody>
            </Table>
          )}
        </Box>

        {/* Results Table - Only Valid Traces */}
        <Box mb={8}>
          <Text
            as="h2"
            fontSize="sm"
            fontWeight="bold"
            mb={4}
            pb={2}
            borderBottom="1px solid"
            borderColor="gray.300"
            display="block"
            className="table-header-blue"
            style={{ color: '#0ea5e9' }}
            sx={{
              color: '#0ea5e9 !important',
              WebkitPrintColorAdjust: 'exact',
              printColorAdjust: 'exact',
              '@media print': {
                display: 'block !important',
                visibility: 'visible !important',
                opacity: '1 !important',
                color: '#0ea5e9 !important',
                fontSize: '14pt !important',
                fontWeight: 'bold !important',
                marginBottom: '16px !important',
                paddingBottom: '8px !important',
                borderBottom: '1px solid #000 !important',
                pageBreakAfter: 'avoid',
                WebkitPrintColorAdjust: 'exact !important',
                printColorAdjust: 'exact !important',
              },
            }}
          >
            نتيجة البحث في قواعد الحاسب الآلي للبصمات
          </Text>
          {(() => {
            const validTraces = reportData.samples
              .flatMap(sample => sample.traces)
              .filter(trace => trace.status === 'Valid');
            
            return validTraces.length === 0 ? (
              <Text textAlign="center" color="gray.500" py={8} fontStyle="italic">
                لا توجد آثار صالحة
              </Text>
            ) : (
              <Table variant="simple" size="sm" sx={{
                tableLayout: 'fixed',
                width: '100%',
                border: '1px solid',
                borderColor: 'gray.300',
                '@media screen': {
                  tableLayout: 'fixed',
                  width: '100%',
                  border: '1px solid',
                  borderColor: 'gray.300',
                },
                '@media print': {
                  tableLayout: 'fixed',
                  width: '100%',
                  border: '1px solid #000 !important',
                },
                '& td, & th': {
                  border: '1px solid',
                  borderColor: 'gray.300',
                  '@media print': {
                    border: '1px solid #000 !important',
                  },
                },
              }}>
                <Thead>
                  <Tr>
                    <Th width="25%" textAlign="center" fontWeight="bold">رقم الأثر</Th>
                    <Th width="25%" textAlign="center" fontWeight="bold">القرار</Th>
                    <Th width="25%" textAlign="center" fontWeight="bold">الاسم</Th>
                    <Th width="25%" textAlign="center" fontWeight="bold">رقم الهوية</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {validTraces.map((trace) => (
                    <Tr 
                      key={trace.id}
                      className={trace.decision === 'Match' ? 'decision-match-row' : undefined}
                      sx={{
                        color: trace.decision === 'Match' ? '#991b1b' : 'inherit',
                        '@media print': {
                          color: trace.decision === 'Match' ? '#991b1b !important' : '#000000',
                        },
                      }}
                    >
                      <Td sx={{
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        whiteSpace: 'normal',
                        textAlign: 'center',
                        '@media print': {
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word',
                          whiteSpace: 'normal',
                          textAlign: 'center',
                        },
                      }}>{trace.traceNumber}</Td>
                      <Td sx={{
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        whiteSpace: 'normal',
                        textAlign: 'center',
                        '@media print': {
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word',
                          whiteSpace: 'normal',
                          textAlign: 'center',
                        },
                      }}>{trace.decision ? (decisionLabels[trace.decision] || trace.decision) : '-'}</Td>
                      <Td sx={{
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        whiteSpace: 'normal',
                        textAlign: 'center',
                        '@media print': {
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word',
                          whiteSpace: 'normal',
                          textAlign: 'center',
                        },
                      }}>{trace.name || '-'}</Td>
                      <Td sx={{
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        whiteSpace: 'normal',
                        textAlign: 'center',
                        '@media print': {
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word',
                          whiteSpace: 'normal',
                          textAlign: 'center',
                        },
                      }}>{trace.identityNumber || '-'}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            );
          })()}
        </Box>

        {/* Attachments Section (before examiners & confirmation) */}
        <Box
          mb={8}
          sx={{
            '@media print': {
              pageBreakInside: 'avoid',
              breakInside: 'avoid',
              WebkitColumnBreakInside: 'avoid',
            },
          }}
        >
          <Text
            as="h2"
            fontSize="sm"
            fontWeight="bold"
            mb={4}
            pb={2}
            borderBottom="1px solid"
            borderColor="gray.300"
            display="block"
            className="table-header-blue"
            style={{ color: '#0ea5e9' }}
            sx={{
              color: '#0ea5e9 !important',
              WebkitPrintColorAdjust: 'exact',
              printColorAdjust: 'exact',
              '@media print': {
                display: 'block !important',
                visibility: 'visible !important',
                opacity: '1 !important',
                color: '#0ea5e9 !important',
                fontSize: '14pt !important',
                fontWeight: 'bold !important',
                marginBottom: '16px !important',
                paddingBottom: '8px !important',
                borderBottom: '1px solid #000 !important',
                pageBreakAfter: 'avoid',
              },
            }}
          >
            المرفقات
          </Text>

          {(() => {
            const caseNumber = reportData.case?.caseNumber || 'غير محدد';
            const refLetter = reportData.case?.referenceLetterNumber || 'غير محدد';
            const letterDate = reportData.case?.letterDateHijriFormatted || 'غير محدد';

            const items = [
              (
                <span>
                  التقرير المصور للقضية رقم (
                  <span dir="ltr" style={{ display: 'inline-block' }}>{caseNumber}</span>
                  ).
                </span>
              ),
              (
                <span>
                  العينات المرفقة بالخطاب رقم (
                  <span dir="ltr" style={{ display: 'inline-block' }}>{refLetter}</span>
                  ) وتاريخ ({letterDate}) رفق تقريرنا هذا .
                </span>
              ),
            ];

            const hasAthr = Boolean(confirmationData.athrReportNumber || confirmationData.athrReportDate);
            if (hasAthr) {
              const athrNo = confirmationData.athrReportNumber || 'غير محدد';
              const athrDateHijri = confirmationData.athrReportDate
                ? formatHijriFromGregorian(confirmationData.athrReportDate)
                : 'غير محدد';
              items.push(
                <span>
                  تقرير القضية في نظام أثر رقم (
                  <span dir="ltr" style={{ display: 'inline-block' }}>{athrNo}</span>
                  ) وتاريخ ({athrDateHijri}) .
                </span>
              );
            }

            const extra = (confirmationData.additionalAttachments || []).filter((x) => String(x).trim().length > 0);
            extra.forEach((text) => items.push(String(text)));

            return (
              <Table
                variant="unstyled"
                size="sm"
                sx={{
                  border: 'none',
                  '& td, & th': {
                    border: 'none !important',
                    padding: '0 !important',
                  },
                  '@media print': {
                    border: 'none !important',
                  },
                }}
              >
                <Tbody>
                  <Tr sx={{ '@media print': { pageBreakInside: 'avoid' } }}>
                    <Td>
                      <VStack align="stretch" spacing={2}>
                        {items.map((item, idx) => (
                          <Text
                            key={idx}
                            fontSize="sm"
                            color="gray.800"
                            sx={{ '@media print': { color: '#000000 !important' } }}
                          >
                            <Text as="span">
                              {formatArabicListNumber(idx + 1)}-{' '}
                            </Text>
                            <Text as="span">{item}</Text>
                          </Text>
                        ))}
                      </VStack>
                    </Td>
                  </Tr>
                </Tbody>
              </Table>
            );
          })()}
        </Box>

        {/* Results Interpretation Section (before examiners & confirmation) */}
        <Box
          mb={8}
          sx={{
            '@media print': {
              pageBreakInside: 'avoid',
              breakInside: 'avoid',
              WebkitColumnBreakInside: 'avoid',
            },
          }}
        >
          <Text
            as="h2"
            fontSize="sm"
            fontWeight="bold"
            mb={4}
            pb={2}
            borderBottom="1px solid"
            borderColor="gray.300"
            display="block"
            className="table-header-blue"
            style={{ color: '#0ea5e9' }}
            sx={{
              color: '#0ea5e9 !important',
              WebkitPrintColorAdjust: 'exact',
              printColorAdjust: 'exact',
              '@media print': {
                display: 'block !important',
                visibility: 'visible !important',
                opacity: '1 !important',
                color: '#0ea5e9 !important',
                fontSize: '14pt !important',
                fontWeight: 'bold !important',
                marginBottom: '12px !important',
                paddingBottom: '8px !important',
                borderBottom: '1px solid #000 !important',
                pageBreakAfter: 'avoid',
              },
            }}
          >
            تفسير النتائج:
          </Text>

          <Table
            variant="unstyled"
            size="sm"
            sx={{
              border: 'none',
              '& td, & th': {
                border: 'none !important',
                padding: '0 !important',
              },
              '@media print': {
                border: 'none !important',
              },
            }}
          >
            <Tbody>
              <Tr sx={{ '@media print': { pageBreakInside: 'avoid' } }}>
                <Td>
                  <VStack align="stretch" spacing={2}>
                    <Text fontSize="sm" color="gray.800" sx={{ '@media print': { color: '#000000 !important' } }}>
                      <Text as="span">{formatArabicListNumber(1)}- </Text>
                      <Text as="span" fontWeight="bold">صالح:</Text>
                      <Text as="span">
                        {' '}
                        تكون بوجود العلامات المميزة للبصمة لإعطاء قرار الإنطباق من عدمه.
                      </Text>
                    </Text>
                    <Text fontSize="sm" color="gray.800" sx={{ '@media print': { color: '#000000 !important' } }}>
                      <Text as="span">{formatArabicListNumber(2)}- </Text>
                      <Text as="span" fontWeight="bold">غير صالح:</Text>
                      <Text as="span">
                        {' '}
                        هي البصمة التي تتعذر إجراء المقارنة الفنية لها بسبب عدم وجود أو اكتمال العلامات المميزة للمقارنة الفنية.
                      </Text>
                    </Text>
                    <Text fontSize="sm" color="gray.800" sx={{ '@media print': { color: '#000000 !important' } }}>
                      <Text as="span">{formatArabicListNumber(3)}- </Text>
                      <Text as="span" fontWeight="bold">لايوجد:</Text>
                      <Text as="span">
                        {' '}
                        تعذر وجود آثار للبصمات.
                      </Text>
                    </Text>
                  </VStack>
                </Td>
              </Tr>
            </Tbody>
          </Table>
        </Box>

        {/* Examiners Table */}
        <Box mb={8}>
          <Text
            as="h2"
            fontSize="sm"
            fontWeight="bold"
            mb={4}
            pb={2}
            borderBottom="1px solid"
            borderColor="gray.300"
            display="block"
            className="table-header-blue"
            style={{ color: '#0ea5e9' }}
            sx={{
              color: '#0ea5e9 !important',
              WebkitPrintColorAdjust: 'exact',
              printColorAdjust: 'exact',
              '@media print': {
                display: 'block !important',
                visibility: 'visible !important',
                opacity: '1 !important',
                color: '#0ea5e9 !important',
                fontSize: '14pt !important',
                fontWeight: 'bold !important',
                marginBottom: '16px !important',
                paddingBottom: '8px !important',
                borderBottom: '1px solid #000 !important',
                pageBreakAfter: 'avoid',
                WebkitPrintColorAdjust: 'exact !important',
                printColorAdjust: 'exact !important',
              },
            }}
          >
            الفاحصين
          </Text>
          {reportData.examiners && reportData.examiners.length === 0 ? (
            <Text textAlign="center" color="gray.500" py={8} fontStyle="italic">
              لا يوجد فاحصين معينين
            </Text>
          ) : (
            <Table variant="simple" size="sm" sx={{
              tableLayout: 'fixed',
              width: '100%',
              border: '1px solid',
              borderColor: 'gray.300',
              '@media screen': {
                tableLayout: 'fixed',
                width: '100%',
                border: '1px solid',
                borderColor: 'gray.300',
              },
              '@media print': {
                tableLayout: 'fixed',
                width: '100%',
                border: '1px solid #000 !important',
              },
              '& td, & th': {
                border: '1px solid',
                borderColor: 'gray.300',
                '@media print': {
                  border: '1px solid #000 !important',
                },
              },
            }}>
              <Thead>
                <Tr>
                  <Th width="50%" textAlign="center" fontWeight="bold">الاسم</Th>
                  <Th width="50%" textAlign="center" fontWeight="bold">التوقيع</Th>
                </Tr>
              </Thead>
              <Tbody>
                {reportData.examiners && reportData.examiners.length > 0 ? (
                  reportData.examiners.map((examiner) => (
                    <Tr key={examiner.id}>
                      <Td sx={{
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        whiteSpace: 'normal',
                        textAlign: 'center',
                        '@media print': {
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word',
                          whiteSpace: 'normal',
                          textAlign: 'center',
                        },
                      }}>{examiner.fullName || examiner.full_name || '-'}</Td>
                      <Td sx={{
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        whiteSpace: 'normal',
                        textAlign: 'center',
                        minHeight: '40px',
                        '@media print': {
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word',
                          whiteSpace: 'normal',
                          textAlign: 'center',
                          minHeight: '40px',
                        },
                      }}></Td>
                    </Tr>
                  ))
                ) : (
                  <Tr>
                    <Td colSpan={2} textAlign="center" color="gray.500" fontStyle="italic">
                      لا يوجد فاحصين معينين
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          )}
      </Box>

        {/* Confirmation Table */}
        <Box mb={8}>
          <Text
            as="h2"
            fontSize="sm"
            fontWeight="bold"
            mb={4}
            pb={2}
            borderBottom="1px solid"
            borderColor="gray.300"
            display="block"
            className="table-header-blue"
            style={{ color: '#0ea5e9' }}
            sx={{
              color: '#0ea5e9 !important',
              WebkitPrintColorAdjust: 'exact',
              printColorAdjust: 'exact',
              '@media print': {
                display: 'block !important',
                visibility: 'visible !important',
                opacity: '1 !important',
                color: '#0ea5e9 !important',
                fontSize: '14pt !important',
                fontWeight: 'bold !important',
                marginBottom: '16px !important',
                paddingBottom: '8px !important',
                borderBottom: '1px solid #000 !important',
                pageBreakAfter: 'avoid',
                WebkitPrintColorAdjust: 'exact !important',
                printColorAdjust: 'exact !important',
              },
            }}
          >
            المصادقة
          </Text>
          <Table variant="simple" size="sm" sx={{
            tableLayout: 'fixed',
            width: '100%',
            border: '1px solid',
            borderColor: 'gray.300',
            '@media screen': {
              tableLayout: 'fixed',
              width: '100%',
              border: '1px solid',
              borderColor: 'gray.300',
            },
            '@media print': {
              tableLayout: 'fixed',
              width: '100%',
              border: '1px solid #000 !important',
            },
            '& td, & th': {
              border: '1px solid',
              borderColor: 'gray.300',
              '@media print': {
                border: '1px solid #000 !important',
              },
            },
          }}>
            <Thead>
              <Tr>
                <Th width="33.33%" textAlign="center" fontWeight="bold">المنصب</Th>
                <Th width="33.33%" textAlign="center" fontWeight="bold">الاسم</Th>
                <Th width="33.33%" textAlign="center" fontWeight="bold">التوقيع</Th>
              </Tr>
            </Thead>
            <Tbody>
              <Tr>
                <Td sx={{
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  whiteSpace: 'normal',
                  textAlign: 'center',
                  '@media print': {
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                    whiteSpace: 'normal',
                    textAlign: 'center',
                  },
                }}>{confirmationData.position || '-'}</Td>
                <Td sx={{
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  whiteSpace: 'normal',
                  textAlign: 'center',
                  '@media print': {
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                    whiteSpace: 'normal',
                    textAlign: 'center',
                  },
                }}>{confirmationData.name || '-'}</Td>
                <Td sx={{
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  whiteSpace: 'normal',
                  textAlign: 'center',
                  minHeight: '40px',
                  '@media print': {
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                    whiteSpace: 'normal',
                    textAlign: 'center',
                    minHeight: '40px',
                  },
                }}></Td>
              </Tr>
            </Tbody>
          </Table>
        </Box>
      </Box>

      {/* Confirmation Modal */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>إدخال بيانات المصادقة</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel>المنصب</FormLabel>
                <Input
                  value={confirmationData.position}
                  onChange={(e) => {
                    const convertedValue = arabicToEnglish(e.target.value);
                    setConfirmationData({ ...confirmationData, position: convertedValue });
                  }}
                  placeholder="أدخل المنصب"
                />
              </FormControl>
              <FormControl>
                <FormLabel>الاسم</FormLabel>
                <Input
                  value={confirmationData.name}
                  onChange={(e) => {
                    const convertedValue = arabicToEnglish(e.target.value);
                    setConfirmationData({ ...confirmationData, name: convertedValue });
                  }}
                  placeholder="أدخل الاسم"
                />
              </FormControl>

              <Box w="100%" borderTop="1px solid" borderColor="gray.200" pt={3} />

              <FormControl>
                <FormLabel>تقرير القضية في نظام أثر (رقم التقرير) - اختياري</FormLabel>
                <Input
                  value={confirmationData.athrReportNumber}
                  onChange={(e) => {
                    const convertedValue = arabicToEnglish(e.target.value);
                    setConfirmationData({ ...confirmationData, athrReportNumber: convertedValue });
                  }}
                  placeholder="أدخل رقم التقرير في نظام أثر"
                />
              </FormControl>

              <FormControl>
                <FormLabel>تقرير القضية في نظام أثر (التاريخ) - اختياري</FormLabel>
                <HijriDatePicker
                  value={confirmationData.athrReportDate}
                  onChange={(date) => setConfirmationData({ ...confirmationData, athrReportDate: date })}
                  placeholder="اختر تاريخ تقرير نظام أثر"
                />
              </FormControl>

              <Box w="100%" borderTop="1px solid" borderColor="gray.200" pt={3} />

              <FormControl>
                <FormLabel>مرفقات إضافية (اختياري)</FormLabel>
                <HStack align="stretch">
                  <Input
                    value={newAttachment}
                    onChange={(e) => setNewAttachment(e.target.value)}
                    placeholder="اكتب نص المرفق ثم اضغط إضافة"
                  />
                  <Button
                    onClick={() => {
                      const value = String(newAttachment).trim();
                      if (!value) return;
                      setConfirmationData({
                        ...confirmationData,
                        additionalAttachments: [...(confirmationData.additionalAttachments || []), value],
                      });
                      setNewAttachment('');
                    }}
                  >
                    إضافة
                  </Button>
                </HStack>

                {confirmationData.additionalAttachments && confirmationData.additionalAttachments.length > 0 && (
                  <VStack align="stretch" spacing={2} mt={3}>
                    {confirmationData.additionalAttachments.map((item, idx) => (
                      <Flex key={idx} justify="space-between" gap={3} align="center">
                        <Text fontSize="sm" flex="1" noOfLines={2}>{item}</Text>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            const next = [...confirmationData.additionalAttachments];
                            next.splice(idx, 1);
                            setConfirmationData({ ...confirmationData, additionalAttachments: next });
                          }}
                        >
                          حذف
                        </Button>
                      </Flex>
                    ))}
                  </VStack>
                )}
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              إلغاء
            </Button>
            <Button colorScheme="primary" onClick={handleConfirmPrint}>
              طباعة
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Container>
    </>
  );
};

export default TechnicalReport;
